import { useEffect, useState } from 'react'

import DeprecatedIcon from '@ui-components/icon'
import colors from '@web-ui-kit/colors'
import Icon from '@web-ui-kit/icon'
import Tag from '@web-ui-kit/tag'
import Tooltip from '@web-ui-kit/tooltip'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import useMediaQuery from '@web-ui-kit/hooks.use-media-query'
import Text from '@web-ui-kit/text'
import Menu from '@web-ui-kit/menu'
import * as Sc from './SubHeader.style'
import NavHeaderSkeleton from './SubHeader.skeleton'
import Toggle from 'components/Toggle'
import useMediaQueryDeprecated from 'hooks/useMediaQuery'
import { addEventToGTM } from 'utils/GoogleTagManager'
import { TAB_TASKS_CLASS_TEACHER_EVENT } from 'pages/ClassroomTasks/utils/events'
import Markdown from 'components/Markdown'

const HOVER_TITLE_DELAY = 1300

const SubHeader = ({
  backward,
  button,
  id,
  infoItem,
  isDemo,
  isLoading,
  sections,
  select,
  tag,
  title,
}) => {
  const [isToggleEnabled, setIsToggleEnabled] = useState(true)
  const [selectedOption, setSelectedOption] = useState(null)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const { goBack, push } = useHistory()
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const { isMobile, isTablet, isLaptop, isDesktop } = useMediaQueryDeprecated()
  const { isExtraSmall } = useMediaQuery()
  const size = !isDesktop ? 'small' : 'big'

  useEffect(() => {
    const selectedIndex = sections?.findIndex(
      (section) => section.route === pathname
    )
    setSelectedOption(sections[selectedIndex])
    setActiveTabIndex(selectedIndex)
  }, [pathname, sections])

  const handleToggleEnabled = () => {
    setIsToggleEnabled(!isToggleEnabled)
  }
  const handleGoBack = () => {
    backward
      ? backward instanceof Function
        ? backward()
        : push(backward)
      : goBack()
  }

  const renderRightSide = () => {
    if (isLoading) return null
    if (button?.text || select) {
      const {
        dataTestId,
        id,
        label,
        options = [],
        onSelect,
        selected,
      } = select || {}
      const renderActions = () => {
        return (
          <Sc.StyledActionsContainer key={1}>
            {select && (
              <Sc.StyledSelect
                dataTestId={dataTestId}
                directionDropdown={isExtraSmall ? 'top' : 'bottom'}
                id={id}
                label={label}
                onChange={onSelect}
                optionKeySelected={selected}
                options={options}
                size='small'
                textWithoutOptions={t('common.feedback.no_results', {
                  keySeparator: '.',
                })}
              />
            )}
            {button?.text && (
              <Sc.StyledButton
                dataTestId={button.dataTestId}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...{ label: button.text }}
                disabled={button.disabled}
                id={button.id}
                label={button.text}
                loading={button.loading}
                onClick={button.onClick}
                size={isExtraSmall ? 'small' : 'big'}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(button?.icon
                  ? {
                      icon: (
                        <Icon
                          fill={button.disabled ? colors.WHITE : colors.BLACK}
                          height='32'
                          icon={button?.icon}
                        />
                      ),
                    }
                  : {})}
              />
            )}
          </Sc.StyledActionsContainer>
        )
      }
      return isExtraSmall ? (
        <Menu
          dataTestId='menu-subheader-actions'
          icon={<DeprecatedIcon height={32} icon='menu' width={32} />}
          id='menu-subheader-actions'
          menuItems={[renderActions()]}
          titleMenu={t('Menu')}
          variant='bottomSheet'
          zIndex={1}
        />
      ) : (
        renderActions()
      )
    }
    if (sections?.length && isDesktop) {
      const textToggle = isToggleEnabled ? `${t('Ocultar')}` : `${t('Mostrar')}`
      return (
        <Toggle
          dataTestId='button-hide'
          onClick={handleToggleEnabled}
          text={textToggle}
        />
      )
    }
    return null
  }

  const renderLeftSide = () => {
    const isSelectedOptionInfoShown = !isToggleEnabled && selectedOption
    return (
      <Sc.LeftSide>
        {isLoading ? (
          <NavHeaderSkeleton />
        ) : (
          <>
            {title &&
              (isDesktop ? (
                <Tooltip
                  align='end'
                  delay={HOVER_TITLE_DELAY}
                  element={
                    <Sc.Title $isDemo={isDemo} id={id} variant='title6'>
                      <Markdown text={title} />
                      {infoItem && ` - ${infoItem}`}
                    </Sc.Title>
                  }
                  id='top-nav-tooltip'
                  position='bottom'
                >
                  {
                    <Text id={id} variant='small'>
                      <Markdown text={title} />
                      {infoItem && ` - ${infoItem}`}
                    </Text>
                  }
                </Tooltip>
              ) : (
                <Sc.Title
                  $isDemo={isDemo}
                  $isLaptop={isLaptop}
                  $isSmall={isMobile}
                  $isTablet={isTablet}
                  id={id}
                  variant={isMobile || isTablet ? 'body' : 'title6'}
                >
                  <Markdown text={title} />
                  {infoItem && ` - ${infoItem}`}
                </Sc.Title>
              ))}
            {tag}
            {isDemo && (
              <Tag
                dataTestId='test-tag-header'
                isMobile={isMobile}
                text={t('Muestra')}
                variant='sample'
              />
            )}
            {!!(isLaptop && isSelectedOptionInfoShown) && (
              <Sc.LeftSideSelectedOption>
                <Sc.Divider />
                <Sc.CourseName variant={'body'}>
                  {selectedOption?.title}
                </Sc.CourseName>
              </Sc.LeftSideSelectedOption>
            )}
          </>
        )}
      </Sc.LeftSide>
    )
  }

  const handleChange = (tab) => {
    const selectedTab = tab
    const tasksRegExp = /^\/classrooms\/[^/]+\/tasks$/
    if (tasksRegExp.test(sections[selectedTab].route)) {
      addEventToGTM(TAB_TASKS_CLASS_TEACHER_EVENT)
    }
    setActiveTabIndex(selectedTab)
    push(sections[selectedTab].route)
  }

  const isCollapsedIn = isToggleEnabled && sections?.length

  return (
    <Sc.StyledTopNavExtended
      $isSmall={isMobile || isTablet}
      $isTablet={isTablet}
      activeTabIndex={activeTabIndex}
      button={{
        dataTestId: 'button-back',
        icon: 'back',
        onClick: handleGoBack,
      }}
      dataTestId='navigation-top-bar'
      isOpen={isCollapsedIn}
      leftContent={renderLeftSide()}
      onClickTab={handleChange}
      rightContent={renderRightSide()}
      sections={sections}
      size={size}
      zIndex={1}
    />
  )
}

SubHeader.defaultProps = {
  backward: '',
  button: {
    dataTestId: '',
    disabled: false,
    loading: false,
    onClick: null,
    text: '',
  },
  id: '',
  infoItem: '',
  isDemo: false,
  isLoading: false,
  sections: [],
  select: null,
  tag: null,
  title: '',
}

SubHeader.propTypes = {
  backward: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  button: PropTypes.shape({
    dataTestId: PropTypes.string,
    disabled: PropTypes.bool,
    icon: PropTypes.string,
    id: PropTypes.string,
    loading: PropTypes.bool,
    onClick: PropTypes.func,
    text: PropTypes.string,
  }),
  id: PropTypes.string,
  infoItem: PropTypes.string,
  isDemo: PropTypes.bool,
  isLoading: PropTypes.bool,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      dataTestId: PropTypes.string.isRequired,
      route: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ),
  select: PropTypes.shape({
    dataTestId: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        dataTestId: PropTypes.string,
        key: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ),
    selected: PropTypes.string,
  }),
  tag: PropTypes.node,
  title: PropTypes.string,
}

export default SubHeader
